import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@material-ui/core/styles';
import Box from '@mui/material/Box';
import { Grid } from '@mui/material';
import DashboardChart from '../../../common/components/charts/DashboardChart';
import {
	NIL_PICK_RATE,
	FIRST_TIME_PICK_RATE,
	PRE_SUBSITUTE_RATE,
	POST_SUBSITUTE_RATE,
	ERROR_SCREEN_MSG_STORE,
	REPLENISHMENT_DATA_NOT_AVAILABLE,
} from '../../../constants/constant';
import { UserPortalContext } from '../../../context/appcontext';
import debounce from 'lodash/debounce';
import {
	getNilPickRate,
	getFirstTimePickRate,
	getPreSubstituteRate,
	getPostSubstituteRate,
} from '../../../redux/actions/itemDrillDownActions';
import { useSelector, useDispatch } from 'react-redux';
import analytics from '../../../helper/analytics';
import tracker from '../../../constants/tracker';
import { styled } from '@mui/material/styles';
import { theme } from '../../../theme/theme';

const Item = styled(Grid)(({ theme }) => ({
	...theme.typography.body2,
	padding: theme.spacing(1),
	textAlign: 'center',
	color: theme.palette.text.secondary,
}));

function Replenishment({
	selectedTab,
	weekFilter,
	selectedFilter,
	startDate,
	endDate,
	itemNum,
	itemName,
}) {
	const [width, setWidth] = useState(0);
	const dispatch = useDispatch();
	const selectedView = useSelector(
		(state) => state.appGuideReducer.selectedView
	);
	const portalUserContext = React.useContext(UserPortalContext);
	const urlParams = new URLSearchParams(window.location.search);
	const ctid = urlParams.get('ctId');

	const makeApiCall = async (weekFilter, startDate, endDate) => {
		if (itemNum) {
			const params = {
				categoryId: '-1',
				period: weekFilter,
				portalUserContext,
				itemNum,
				selectedFilter,
				startDate,
				endDate,
			};
			dispatch(getNilPickRate(params));
			dispatch(getFirstTimePickRate(params));
			dispatch(getPreSubstituteRate(params));
			dispatch(getPostSubstituteRate(params));
		}
	};

	const kpiName = useSelector((state) => state.salesReducer.kpiName);

	const category = ctid;

	useEffect(() => {
		makeApiCall(weekFilter, startDate, endDate);
	}, [weekFilter, startDate, endDate, selectedView]);

	const {
		nilPickRateData,
		nilPickRateLoading,
		nilPickRateErrorCode,
		firstTimePickRateData,
		firstTimePickRateLoading,
		firstTimePickRateErrorCode,
		preSubtituteRateData,
		preSubtituteRateLoading,
		preSubtituteRateErrorCode,
		postSubtituteRateData,
		postSubtituteRateLoading,
		postSubtituteRateErrorCode,
	} = useSelector((state) => state.itemDrillDownReducer);

	useEffect(() => {
		const updateSize = debounce(() => {
			setWidth(window.innerHeight + window.innerWidth);
		}, 1500);
		window.addEventListener('resize', updateSize);
		updateSize();
		const pageid = `${kpiName}Item${selectedTab}`
			.replaceAll(/\s/g, '')
			.toUpperCase();
		analytics.trackPageInfo(tracker.PAGE_ID[pageid]);
		return () => window.removeEventListener('resize', updateSize);
	}, []);
	return (
		<ThemeProvider theme={theme}>
			<Box sx={{ flexGrow: 1 }}>
				<Grid container>
					<Grid data-test-id="nil-pick-rate" item xs={12} sm={6} md={6} lg={6}>
						<Item>
							<DashboardChart
								chartData={nilPickRateData?.nilPickRateData}
								chartTitle={NIL_PICK_RATE}
								chartName={'NIL PICK RATE'}
								weekFilter={weekFilter}
								isLoading={nilPickRateLoading}
								errorMsg={
									(nilPickRateErrorCode === 0 &&
										!nilPickRateData?.nilPickRateData) ||
									nilPickRateErrorCode === 204
										? REPLENISHMENT_DATA_NOT_AVAILABLE
										: ERROR_SCREEN_MSG_STORE
								}
								updatedWidth={width}
								category={category}
								selectedFilter={selectedFilter}
								showExploreButton={false}
								isReplenishment={true}
								itemNum={itemNum}
								item={itemName}
								showTitleInErrorContent={false}
								selectedTab={selectedTab}
							/>
						</Item>
					</Grid>
					<Grid
						data-test-id="first-time-pick-rate"
						item
						xs={12}
						sm={6}
						md={6}
						lg={6}>
						<Item>
							<DashboardChart
								chartData={firstTimePickRateData?.firstTimePickRateData}
								chartTitle={FIRST_TIME_PICK_RATE}
								chartName={'First Time Pick Rate'}
								weekFilter={weekFilter}
								isLoading={firstTimePickRateLoading}
								errorMsg={
									(firstTimePickRateErrorCode === 0 &&
										!firstTimePickRateData?.firstTimePickRateData) ||
									firstTimePickRateErrorCode === 204
										? REPLENISHMENT_DATA_NOT_AVAILABLE
										: ERROR_SCREEN_MSG_STORE
								}
								updatedWidth={width}
								category={category}
								selectedFilter={selectedFilter}
								showExploreButton={false}
								isReplenishment={true}
								itemNum={itemNum}
								item={itemName}
								showTitleInErrorContent={false}
								selectedTab={selectedTab}
							/>
						</Item>
					</Grid>
					<Grid
						data-test-id="pre-substitute-rate"
						item
						xs={12}
						sm={6}
						md={6}
						lg={6}>
						<Item>
							<DashboardChart
								chartData={preSubtituteRateData?.preSubtituteRateData}
								chartTitle={PRE_SUBSITUTE_RATE}
								chartName={'Pre Substitute Rate'}
								weekFilter={weekFilter}
								isLoading={preSubtituteRateLoading}
								errorMsg={
									(preSubtituteRateErrorCode === 0 &&
										!preSubtituteRateData?.preSubtituteRateData) ||
									preSubtituteRateErrorCode === 204
										? REPLENISHMENT_DATA_NOT_AVAILABLE
										: ERROR_SCREEN_MSG_STORE
								}
								updatedWidth={width}
								category={category}
								selectedFilter={selectedFilter}
								showExploreButton={false}
								isReplenishment={true}
								itemNum={itemNum}
								item={itemName}
								showTitleInErrorContent={false}
								selectedTab={selectedTab}
							/>
						</Item>
					</Grid>
					<Grid
						data-test-id="post-substitute-rate"
						item
						xs={12}
						sm={6}
						md={6}
						lg={6}>
						<Item>
							<DashboardChart
								chartData={postSubtituteRateData?.postSubtituteRateData}
								chartTitle={POST_SUBSITUTE_RATE}
								chartName={'Post Substitute Rate'}
								weekFilter={weekFilter}
								isLoading={postSubtituteRateLoading}
								errorMsg={
									(postSubtituteRateErrorCode === 0 &&
										!postSubtituteRateData?.postSubtituteRateData) ||
									postSubtituteRateErrorCode === 204
										? REPLENISHMENT_DATA_NOT_AVAILABLE
										: ERROR_SCREEN_MSG_STORE
								}
								updatedWidth={width}
								category={category}
								selectedFilter={selectedFilter}
								showExploreButton={false}
								isReplenishment={true}
								itemNum={itemNum}
								item={itemName}
								showTitleInErrorContent={false}
								selectedTab={selectedTab}
							/>
						</Item>
					</Grid>
				</Grid>
			</Box>
		</ThemeProvider>
	);
}

Replenishment.propTypes = {
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	selectedTab: PropTypes.string,
	selectedFilter: PropTypes.string,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	itemNum: PropTypes.string,
	itemName: PropTypes.string,
};

export default Replenishment;
